import React, { useEffect, useState } from "react";
import { X, Calendar, Clock, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const ACTIVITY_TYPES = [
  { value: "call", label: "Call" },
  { value: "meeting", label: "Meeting" },
  { value: "email", label: "Email" },
  { value: "follow_up", label: "Follow-up" },
  { value: "demo", label: "Demo" },
  { value: "task", label: "Task" },
];

function nextHourIso() {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  // local format YYYY-MM-DDTHH:mm
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ScheduleActivityDialog({ open, leadId, leadCompany, onClose, onSaved, initial = null, canAssignToOthers = false }) {
  const [form, setForm] = useState({
    lead_id: leadId || "",
    type: "call",
    scheduled_at: nextHourIso(),
    notes: "",
    assignee_id: "",
  });
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      // editing existing — convert ISO to local datetime-local
      const dt = new Date(initial.scheduled_at);
      const pad = (n) => String(n).padStart(2, "0");
      setForm({
        lead_id: initial.lead_id,
        type: initial.type,
        scheduled_at: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
        notes: initial.notes || "",
        assignee_id: initial.assignee_id || "",
      });
    } else {
      setForm({ lead_id: leadId || "", type: "call", scheduled_at: nextHourIso(), notes: "", assignee_id: "" });
    }
    (async () => {
      try {
        if (canAssignToOthers) {
          const { data } = await api.get("/users");
          setUsers(data.filter((u) => u.active !== false));
        }
        if (!leadId) {
          const { data } = await api.get("/leads?limit=200");
          setLeads(data.items || []);
        }
      } catch {}
    })();
  }, [open, leadId, initial, canAssignToOthers]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.lead_id) return toast.error("Lead is required");
    setSaving(true);
    try {
      // Convert local datetime-local to ISO UTC
      const iso = new Date(form.scheduled_at).toISOString();
      const body = {
        lead_id: form.lead_id,
        type: form.type,
        scheduled_at: iso,
        notes: form.notes || null,
      };
      if (form.assignee_id) body.assignee_id = form.assignee_id;

      if (initial) {
        await api.patch(`/scheduled-activities/${initial.id}`, body);
        toast.success("Activity updated");
      } else {
        await api.post("/scheduled-activities", body);
        toast.success("Activity scheduled");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="schedule-activity-dialog"
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">{initial ? "Edit activity" : "Schedule activity"}</h3>
            {leadCompany && <p className="text-xs text-slate-500 mt-0.5">For: {leadCompany}</p>}
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-3">
          {!leadId && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Lead</label>
              <select data-testid="sched-lead-select" value={form.lead_id} onChange={(e) => setForm({ ...form, lead_id: e.target.value })} required className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md">
                <option value="">Select a lead…</option>
                {leads.map((l) => <option key={l.id} value={l.id}>{l.company_name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Activity type</label>
            <div className="grid grid-cols-3 gap-2">
              {ACTIVITY_TYPES.map((t) => (
                <button
                  type="button"
                  key={t.value}
                  data-testid={`sched-type-${t.value}`}
                  onClick={() => setForm({ ...form, type: t.value })}
                  className={`px-2 py-1.5 text-xs font-medium rounded-md border ${form.type === t.value ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-700 border-slate-200"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">When</label>
            <input
              data-testid="sched-datetime"
              type="datetime-local"
              required
              value={form.scheduled_at}
              onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md"
            />
          </div>
          {canAssignToOthers && users.length > 0 && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Assign to (optional)</label>
              <select data-testid="sched-assignee" value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md">
                <option value="">Myself</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Notes</label>
            <textarea
              data-testid="sched-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="What's the goal of this activity?"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-200 bg-white">Cancel</button>
          <button
            data-testid="sched-submit"
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-medium"
          >
            {saving ? "Saving…" : (initial ? "Save changes" : "Schedule activity")}
          </button>
        </div>
      </form>
    </div>
  );
}
